import type {
  Project,
  Panel,
  Inverter,
  Battery,
  EVCharger,
} from "@/types/projectTypes";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  Document,
  Paragraph,
  TextRun,
  PageOrientation,
  AlignmentType,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  convertInchesToTwip,
  HeightRule,
  Header,
  Footer,
  Packer,
  IImageOptions,
} from "docx";
import { saveAs } from "file-saver";

// Convert mm to inches (DOCX uses inches)
const mmToInches = (mm: number) => mm / 25.4;

// Convert pixels to points (for font sizes)
const pxToPoints = (px: number) => px * 0.75;

// Helper function to convert HTML color to RGB
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Helper function to create image options
const createImageOptions = (data: string | Buffer): IImageOptions => ({
  data,
  transformation: {
    width: 200,
    height: 100,
  },
  type: "png",
});

export const generateDOCXReport = async (
  project: Project,
  includeSections: Record<string, boolean>,
  selectedPanels: Panel[],
  selectedInverters: Inverter[],
  mapScreenshot: string | null,
  consumptionData: {
    totalF1: number;
    totalF2: number;
    totalF3: number;
    grandTotal: number;
  },
  selectedBatteries: Battery[] = [],
  selectedEVChargers: EVCharger[] = []
) => {
  try {
    // Create new document with A4 page size
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              size: {
                width: convertInchesToTwip(mmToInches(210)), // A4 width
                height: convertInchesToTwip(mmToInches(297)), // A4 height
              },
              margin: {
                top: convertInchesToTwip(mmToInches(20)),
                right: convertInchesToTwip(mmToInches(20)),
                bottom: convertInchesToTwip(mmToInches(20)),
                left: convertInchesToTwip(mmToInches(20)),
              },
            },
          },
          children: await generateDocumentContent(
            project,
            includeSections,
            selectedPanels,
            selectedInverters,
            mapScreenshot,
            consumptionData,
            selectedBatteries,
            selectedEVChargers
          ),
        },
      ],
    });

    // Generate blob using browser-compatible method
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${project.project_name || "Solar-Project"}-Report.docx`);
  } catch (error) {
    console.error("Error generating DOCX report:", error);
    throw error;
  }
};

// Helper function to generate document content
async function generateDocumentContent(
  project: Project,
  includeSections: Record<string, boolean>,
  selectedPanels: Panel[],
  selectedInverters: Inverter[],
  mapScreenshot: string | null,
  consumptionData: {
    totalF1: number;
    totalF2: number;
    totalF3: number;
    grandTotal: number;
  },
  selectedBatteries: Battery[],
  selectedEVChargers: EVCharger[]
) {
  const content: any[] = [];

  // Page 1 - Project Overview
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: project.project_name || "Solar Project",
          size: 32,
          bold: true,
          color: "4B91F1",
        }),
      ],
      spacing: { before: 400, after: 400 },
      alignment: AlignmentType.CENTER,
    })
  );

  // Project Information Table
  content.push(
    new Table({
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Client Name:",
                      bold: true,
                    }),
                    new TextRun({
                      text: ` ${project.client_name || "N/A"}`,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Service Type:",
                      bold: true,
                    }),
                    new TextRun({
                      text: ` ${project.service_type || "N/A"}`,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Address:",
                      bold: true,
                    }),
                    new TextRun({
                      text: ` ${project.address || "N/A"}`,
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    })
  );

  // Map Screenshot
  if (mapScreenshot) {
    content.push(
      new Paragraph({
        children: [new ImageRun(createImageOptions(mapScreenshot))],
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 400 },
      })
    );
  }

  // Page 2 - Components and Consumption
  if (
    includeSections.panels ||
    includeSections.inverters ||
    includeSections.batteries ||
    includeSections.evChargers
  ) {
    content.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "System Components",
            size: 28,
            bold: true,
            color: "4B91F1",
          }),
        ],
        spacing: { before: 400, after: 400 },
        alignment: AlignmentType.CENTER,
      })
    );

    // Panels Section
    if (includeSections.panels && selectedPanels.length > 0) {
      content.push(await generatePanelsSection(selectedPanels));
    }

    // Inverters Section
    if (includeSections.inverters && selectedInverters.length > 0) {
      content.push(await generateInvertersSection(selectedInverters));
    }

    // Batteries Section
    if (includeSections.batteries && selectedBatteries.length > 0) {
      content.push(await generateBatteriesSection(selectedBatteries));
    }

    // EV Chargers Section
    if (includeSections.evChargers && selectedEVChargers.length > 0) {
      content.push(await generateEVChargersSection(selectedEVChargers));
    }
  }

  // Consumption Data Section
  if (consumptionData) {
    content.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Consumption Analysis",
            size: 28,
            bold: true,
            color: "4B91F1",
          }),
        ],
        spacing: { before: 400, after: 400 },
        alignment: AlignmentType.CENTER,
      })
    );
    content.push(await generateConsumptionSection(consumptionData));
  }

  // Page 3 - Financial Details (if applicable)
  if (project.service_type === "complete_solution") {
    content.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Financial Details",
            size: 28,
            bold: true,
            color: "4B91F1",
          }),
        ],
        spacing: { before: 400, after: 400 },
        alignment: AlignmentType.CENTER,
      })
    );

    // Add financial details table
    content.push(
      new Table({
        width: {
          size: 100,
          type: WidthType.PERCENTAGE,
        },
        borders: {
          top: { style: BorderStyle.SINGLE },
          bottom: { style: BorderStyle.SINGLE },
          left: { style: BorderStyle.SINGLE },
          right: { style: BorderStyle.SINGLE },
        },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Total System Cost",
                        bold: true,
                      }),
                    ],
                  }),
                ],
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `${project.financial_data?.final_price || 0} €`,
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        ],
      })
    );
  }

  return content;
}

// Helper function to generate panels section
async function generatePanelsSection(panels: Panel[]): Promise<Paragraph> {
  return new Paragraph({
    children: [
      new TextRun({
        text: "Solar Panels",
        size: 24,
        bold: true,
        color: "4B91F1",
      }),
      ...panels.map(
        (panel) =>
          new TextRun({
            text: `\n• ${panel.manufacturer} ${panel.model_number}\n  Power: ${panel.power}W\n  Efficiency: ${panel.efficiency}%\n  Dimensions: ${panel.length}mm x ${panel.width}mm`,
            size: 16,
          })
      ),
    ],
    spacing: { before: 400, after: 200 },
  });
}

// Helper function to generate inverters section
async function generateInvertersSection(
  inverters: Inverter[]
): Promise<Paragraph> {
  return new Paragraph({
    children: [
      new TextRun({
        text: "Inverters",
        size: 24,
        bold: true,
        color: "4B91F1",
      }),
      ...inverters.map(
        (inverter) =>
          new TextRun({
            text: `\n• ${inverter.manufacturer} ${inverter.model_number}\n  Power: ${inverter.nominal_dc_power}W\n  Efficiency: ${inverter.max_efficiency}%\n  MPPT Channels: ${inverter.number_of_mppts}`,
            size: 16,
          })
      ),
    ],
    spacing: { before: 400, after: 200 },
  });
}

// Helper function to generate batteries section
async function generateBatteriesSection(
  batteries: Battery[]
): Promise<Paragraph> {
  return new Paragraph({
    children: [
      new TextRun({
        text: "Batteries",
        size: 24,
        bold: true,
        color: "4B91F1",
      }),
      ...batteries.map(
        (battery) =>
          new TextRun({
            text: `\n• ${battery.manufacturer} ${battery.model_number}\n  Capacity: ${battery.nominal_capacity}kWh\n  Voltage: ${battery.nominal_voltage}V\n  Cycles: ${battery.cycle_life}`,
            size: 16,
          })
      ),
    ],
    spacing: { before: 400, after: 200 },
  });
}

// Helper function to generate EV chargers section
async function generateEVChargersSection(
  chargers: EVCharger[]
): Promise<Paragraph> {
  return new Paragraph({
    children: [
      new TextRun({
        text: "EV Chargers",
        size: 24,
        bold: true,
        color: "4B91F1",
      }),
      ...chargers.map(
        (charger) =>
          new TextRun({
            text: `\n• ${charger.manufacturer} ${charger.model_number}\n  Power: ${charger.max_power}kW\n  Connector Type: ${charger.connector_type}\n  Protection: ${charger.protection_rating}`,
            size: 16,
          })
      ),
    ],
    spacing: { before: 400, after: 200 },
  });
}

// Helper function to generate consumption section
async function generateConsumptionSection(consumptionData: {
  totalF1: number;
  totalF2: number;
  totalF3: number;
  grandTotal: number;
}): Promise<Paragraph> {
  return new Paragraph({
    children: [
      new TextRun({
        text: "Consumption Data",
        size: 24,
        bold: true,
        color: "4B91F1",
      }),
      new TextRun({
        text: `\n• Total F1: ${consumptionData.totalF1.toFixed(2)} kWh`,
        size: 16,
      }),
      new TextRun({
        text: `\n• Total F2: ${consumptionData.totalF2.toFixed(2)} kWh`,
        size: 16,
      }),
      new TextRun({
        text: `\n• Total F3: ${consumptionData.totalF3.toFixed(2)} kWh`,
        size: 16,
      }),
      new TextRun({
        text: `\n• Grand Total: ${consumptionData.grandTotal.toFixed(2)} kWh`,
        size: 16,
        bold: true,
      }),
    ],
    spacing: { before: 400, after: 200 },
  });
}

export const generatePDFReport = async (
  project: Project,
  includeSections: Record<string, boolean>,
  selectedPanels: Panel[],
  selectedInverters: Inverter[],
  mapScreenshot: string | null,
  consumptionData: {
    totalF1: number;
    totalF2: number;
    totalF3: number;
    grandTotal: number;
  },
  selectedBatteries: any[] = [],
  selectedEVChargers: any[] = []
) => {
  try {
    // Create a new PDF document
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Get all report pages
    const pages = document.querySelectorAll(".report-page");

    // A4 dimensions in mm
    const pageWidth = 210;
    const pageHeight = 297;

    // Process each page
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i] as HTMLElement;

      // Add new page for all pages except the first one
      if (i > 0) {
        pdf.addPage();
      }

      // Calculate scale based on the actual page width in pixels vs A4 width in mm
      // This ensures we maintain the same proportions as the preview
      const pixelsPerMm = page.offsetWidth / 210; // 210 is A4 width in mm
      const scale = 10 / pixelsPerMm;

      // Convert the page to canvas
      const canvas = await html2canvas(page, {
        scale: scale, // Use the calculated scale
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: page.offsetWidth,
        height: page.offsetHeight,
      });

      // Add the page to PDF maintaining A4 dimensions
      pdf.addImage(
        canvas.toDataURL("image/jpeg", 1.0),
        "JPEG",
        0,
        0,
        pageWidth,
        pageHeight
      );
    }

    // Save the PDF
    pdf.save(`${project.project_name || "Solar-Project"}-Report.pdf`);
  } catch (error) {
    console.error("Error generating PDF report:", error);
    throw error;
  }
};
